import { useState } from "react";
import { AlertTriangle, ArrowRight, Bell, CheckCircle2, Clock, HelpCircle, MessageSquare, PowerOff, RefreshCw, Server, Users } from "lucide-react";
import { useSystemHealthProblems } from "../../lib/api";
import { FailedToLoad, Heading, Loading, endpoint, focusRing } from "./healthUi";
import { formatWhen, ownerLabel } from "./healthFormat";

// Overview tab: everything that went wrong, from every other tab, in one place. A
// failure should not have to be found by opening the right row of the right tab.
//   status banner -> five areas -> the problems themselves

const AREA_ICONS = { templates: MessageSquare, escalations: Bell, groups: Users, jobs: Clock, machine: Server };

// One look per state, used by the banner and the area cards.
const TONES = {
  ok: { ink: "#166534", soft: "#ECFDF3", line: "#BBF7D0", solid: "#16A34A" },
  bad: { ink: "#991B1B", soft: "#FEF2F2", line: "#FECACA", solid: "#DC2626" },
  unknown: { ink: "#92400E", soft: "#FFFBEB", line: "#FDE68A", solid: "#D97706" },
};

const BANNERS = {
  ok: { tone: "ok", icon: CheckCircle2, title: () => "Everything is working" },
  attention: {
    tone: "bad",
    icon: AlertTriangle,
    title: (d) => `${d.total} ${d.total === 1 ? "thing needs" : "things need"} attention`,
  },
  down: { tone: "bad", icon: PowerOff, title: () => "Something has stopped running" },
  unknown: { tone: "unknown", icon: HelpCircle, title: () => "Some of this page could not be checked" },
};

const card = "rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_4px_12px_rgba(16,24,40,0.08)]";
const sentence = (text) => `${String(text || "").trim().replace(/[.\s]+$/, "")}.`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function Banner({ data }) {
  const banner = BANNERS[data.state] || BANNERS.ok;
  const tone = TONES[banner.tone];
  const Icon = banner.icon;
  const recorded = data.recording?.last_call_at;
  return (
    <header role="status" className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <Icon size={18} aria-hidden="true" className="flex-none" style={{ color: tone.solid }} />
        <Heading className="text-[15px] font-semibold text-[#111827]">{banner.title(data)}</Heading>      </div>
      {recorded ? (
        <p className="text-[13px] text-[#6B7280]">Last send recorded {formatWhen(recorded)}</p>
      ) : (
        <p className="text-[13px] font-medium text-[#991B1B]">No send has been recorded yet, so this page cannot see failures.</p>
      )}
    </header>
  );
}

// One area: how many problems, what was looked at, and a way into its tab.
function AreaCard({ area, picked, pick, openTab }) {
  const tone = TONES[area.state] || TONES.ok;
  const Icon = AREA_ICONS[area.key] || Server;
  const status =
    area.state === "unknown" ? "Could not check" : area.count ? plural(area.count, "problem", "problems") : "All fine";
  const body = (
    <>
      <span className="flex items-center justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: tone.soft, color: tone.solid }}>
          <Icon size={20} aria-hidden="true" />
        </span>
        <span className="text-[22px] font-semibold leading-none tabular-nums" style={{ color: area.count ? tone.ink : "#D1D5DB" }}>
          {area.state === "unknown" ? "?" : area.count}
        </span>
      </span>
      <span className="mt-4 block text-[15px] font-semibold text-[#111827]">{area.label}</span>
      <span className="mt-1 flex items-center gap-1.5 text-[13px] font-medium" style={{ color: tone.ink }}>
        <span className="h-2 w-2 rounded-full" style={{ background: tone.solid }} />
        {status}
      </span>
      <span className="mt-1 block min-h-[18px] text-[12px] text-[#6B7280]">{area.note}</span>
    </>
  );
  const active = picked === area.key;
  return (
    <li
      className={`${card} flex flex-col overflow-hidden transition-shadow hover:shadow-[0_6px_20px_rgba(16,24,40,0.08)]`}
      style={active ? { borderColor: tone.solid, boxShadow: `0 0 0 1px ${tone.solid}` } : undefined}
    >
      {area.count ? (
        <button
          type="button"
          aria-pressed={active}
          aria-label={`${area.label}: ${status}. Show ${active ? "everything" : "only these"}`}
          onClick={() => pick(active ? "" : area.key)}
          className={`flex w-full flex-1 cursor-pointer flex-col items-stretch p-4 text-left ${focusRing}`}
        >
          {body}
        </button>
      ) : (
        <div className="flex flex-1 flex-col items-stretch p-4">{body}</div>
      )}
      <button
        type="button"
        onClick={() => openTab(area.tab)}
        aria-label={`Open ${area.label}`}
        className={`flex cursor-pointer items-center justify-between border-t border-[#F3F4F6] px-4 py-2.5 text-[13px] font-medium text-[#2667B5] hover:bg-[#F9FAFB] ${focusRing}`}
      >
        Open
        <ArrowRight size={15} aria-hidden="true" />
      </button>
    </li>
  );
}

function Problem({ problem, label }) {
  const Icon = AREA_ICONS[problem.area] || Server;
  const tone = problem.will_retry ? TONES.unknown : TONES.bad;
  return (
    <li className="flex gap-4 py-4">
      <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-xl" style={{ background: tone.soft, color: tone.solid }}>
        <Icon size={18} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <p className="text-[14px] font-semibold text-[#111827]">{problem.what}</p>
          <span
            className="inline-flex flex-none items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold"
            style={{ background: tone.soft, color: tone.ink }}
          >
            {problem.will_retry && <RefreshCw size={12} aria-hidden="true" />}
            {problem.will_retry ? "Will try again" : `${ownerLabel(problem.owner)} to fix`}
          </span>
        </div>
        <p className="mt-0.5 text-[12px] text-[#6B7280]">
          {[label, problem.where, problem.at && formatWhen(problem.at)].filter(Boolean).join(" · ")}
        </p>
        <p className="mt-1.5 text-[13px] text-[#374151]">{sentence(problem.meaning)}</p>
      </div>
    </li>
  );
}

function Problems({ data, picked, pick }) {
  const labels = Object.fromEntries(data.areas.map((a) => [a.key, a.label]));
  const shown = picked ? data.problems.filter((p) => p.area === picked) : data.problems;
  const filters = [{ key: "", label: "All", count: data.total }, ...data.areas.filter((a) => a.count).map((a) => ({ key: a.key, label: a.label, count: a.count }))];
  return (
    <section className={`${card} p-5 sm:p-6`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-[18px] font-semibold text-[#111827]">Problems</h2>
        <div className="flex flex-wrap gap-1 rounded-xl bg-[#F3F4F6] p-1" role="group" aria-label="Show problems from">
          {filters.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              aria-pressed={picked === f.key}
              onClick={() => pick(f.key)}
              className={`h-9 cursor-pointer rounded-lg px-3 text-[13px] font-medium ${picked === f.key ? "bg-white text-[#111827] shadow-[0_1px_2px_rgba(16,24,40,0.08)]" : "text-[#4B5563] hover:text-[#111827]"} ${focusRing}`}
            >
              {f.label} <span className="tabular-nums opacity-70">{f.count}</span>
            </button>
          ))}
        </div>
      </div>
      <ul className="mt-3 divide-y divide-[#F3F4F6]">
        {shown.map((p, i) => (
          <Problem key={`${p.area}-${p.at}-${p.what}-${p.where}-${i}`} problem={p} label={labels[p.area]} />
        ))}
      </ul>
      {!picked && data.total > data.problems.length && (
        <p className="mt-3 text-[13px] text-[#6B7280]">
          Showing the first {data.problems.length} of {data.total}. Open an area above to see the rest.
        </p>
      )}
    </section>
  );
}

export default function HealthOverview({ scope, range, openTab }) {
  const [picked, setPicked] = useState("");
  const { data, isLoading, error, refetch } = useSystemHealthProblems({ ...scope, from: range.from() });
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  return (
    <div title={endpoint("problems")} className="mt-6 flex flex-col gap-5">
      <Banner data={data} />
      <ul aria-label="Areas" className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-5">
        {data.areas.map((a) => (
          <AreaCard key={a.key} area={a} picked={picked} pick={setPicked} openTab={openTab} />
        ))}
      </ul>
      {data.total > 0 && <Problems data={data} picked={picked} pick={setPicked} />}
    </div>
  );
}
